import React, { useState } from "react";
import { motion } from "framer-motion";
import { Zap, BookOpen, X, Coffee, Snowflake, CakeSlice, CalendarDays, Globe, Flame, MoreHorizontal } from "lucide-react";
import { CONSTANTS } from "../../data";
import { ASSETS } from "../../assetManifest.js";

interface RecipeBookModalProps {
  show: boolean;
  onClose: () => void;
  discoveredRecipes: string[];
  lastDiscovery: { recipeId: string; timestamp: number } | null;
  aiGeneratedRecipes: { [key: string]: any };
  nitroUnlocked: boolean;
  allItemsMap: Map<string, any>;
  inventory: { [key: string]: number };
  activeSeason: any;
}

const RecipeBookModal: React.FC<RecipeBookModalProps> = ({
  show,
  onClose,
  discoveredRecipes,
  lastDiscovery,
  aiGeneratedRecipes,
  nitroUnlocked,
  allItemsMap,
  inventory,
  activeSeason,
}) => {
  const [activeTab, setActiveTab] = useState('ESPRESSO');

  if (!show) return null;

  const getRecipeCategory = (recipe) => {
    const resultTags = CONSTANTS.ITEM_TAGS[recipe.result.id] || [];
    if (resultTags.includes('seasonal')) return 'SEASONAL';
    if (resultTags.includes('dessert-beverage')) return 'DESSERT';
    if (resultTags.includes('fusion')) return 'FUSION';
    if (resultTags.includes('spice')) return 'SPICED';
    if (resultTags.includes('iced')) return 'ICED';
    if (resultTags.includes('strong')) return 'ESPRESSO';
    return 'OTHER';
  };

  const categorizedRecipes = Object.entries(CONSTANTS.RECIPES).reduce((acc, [recipeId, recipe]) => {
    const category = getRecipeCategory(recipe);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push([recipeId, recipe]);
    return acc;
  }, {});

  const tabs = [
    { key: 'SEASONAL', name: 'Seasonal', icon: <CalendarDays size={14} /> },
    { key: 'DESSERT', name: 'Desserts', icon: <CakeSlice size={14} /> },
    { key: 'FUSION', name: 'Fusion', icon: <Globe size={14} /> },
    { key: 'SPICED', name: 'Spiced', icon: <Flame size={14} /> },
    { key: 'ICED', name: 'Iced', icon: <Snowflake size={14} /> },
    { key: 'ESPRESSO', name: 'Espresso', icon: <Coffee size={14} /> },
    { key: 'OTHER', name: 'Other', icon: <MoreHorizontal size={14} /> },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-surface rounded-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl border border-border-color"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4 border-b border-border-color flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2"><BookOpen /> Recipe Book</h2>
          <button 
            onClick={onClose} 
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-bg-main min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-border-color flex-shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm flex items-center justify-center gap-1 sm:gap-2 transition-colors relative min-h-[44px] ${
                activeTab === tab.key
                  ? 'text-primary-action'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-main'
              }`}
            >
              {tab.icon} <span className="hidden sm:inline">{tab.name}</span>
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-action"></div>
              )}
            </button>
          ))}
        </div>

        <div className="modal-content p-3 sm:p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {(categorizedRecipes[activeTab] || []).map(([recipeId, recipe]) => {
              const discovered = discoveredRecipes.includes(recipeId);
              const isNew = lastDiscovery?.recipeId === recipeId;
              const isNitro = recipe.ingredients.includes('nitro_tap');
              const resultSprite = (ASSETS as any).items?.[recipe.result.id]?.path || (ASSETS as any).images?.defaultItem?.path;
              return (
                <div
                  key={recipeId}
                  className={`p-3 rounded-lg border transition-all flex items-start gap-3 ${
                    isNew ? 'highlight-recipe border-accent' : 
                    discovered ? 'bg-green-50 border-success/30' : 
                    isNitro && !nitroUnlocked ? 'bg-gray-100 border-gray-300' :
                    'bg-bg-main border-border-color'
                  }`}
                >
                  <img src={resultSprite} alt={recipe.result.name} className="w-10 h-10 sm:w-12 sm:h-12 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {discovered ? (
                      <>
                        <div className="font-semibold text-text-primary text-sm sm:text-base">{recipe.result.name}</div>
                        <div className="text-xs sm:text-sm text-text-secondary mb-1 break-words">{recipe.result.description}</div>
                        <div className="text-xs text-text-secondary break-words">
                          {(recipe.ingredients.map((ing: string) => allItemsMap.get(ing)?.name || ing)).join(' + ')}
                        </div>
                      </>
                    ) : isNitro && !nitroUnlocked ? (
                      <div className="text-gray-400 text-sm">🔒 Nitro recipes locked</div>
                    ) : (
                      <>
                        <div className="font-semibold text-text-primary text-gray-400 text-sm sm:text-base">Undiscovered Recipe</div>
                        <div className="text-xs sm:text-sm text-text-secondary mb-1 text-gray-400 break-words">{recipe.result.description}</div>
                        <div className="text-xs text-text-secondary break-words">
                          {recipe.ingredients.map((ingId) => {
                            const item = allItemsMap.get(ingId);
                            if (!item) return '???';
                            const category = Object.values(CONSTANTS.INVENTORY_CATEGORIES).find(cat => cat.types.includes(item.type));
                            return category ? category.name.replace(/s$/, '') : 'Item';
                          }).join(' + ')}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {(!categorizedRecipes[activeTab] || categorizedRecipes[activeTab].length === 0) && (
              <div className="text-text-secondary text-center py-8">No recipes in this category yet.</div>
            )}
          </div>

          {Object.keys(aiGeneratedRecipes).length > 0 && (
            <>
              <h3 className="text-base sm:text-lg font-bold text-text-primary mt-6 mb-2 border-t border-border-color pt-4 flex items-center gap-2"><Zap /> AI Discoveries</h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {Object.values(aiGeneratedRecipes).map((recipe: any) => {
                  const resultSprite = (ASSETS as any).items?.[recipe.result.id]?.path || (ASSETS as any).images?.defaultItem?.path;
                  return (
                    <div
                      key={recipe.id}
                      className="p-3 rounded-lg border bg-indigo-50 border-indigo-200 flex items-start gap-3"
                    >
                      <img src={resultSprite} alt={recipe.result.name} className="w-10 h-10 sm:w-12 sm:h-12 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold flex items-center gap-2 text-indigo-800 text-sm sm:text-base">{recipe.result.name} <Zap size={14} className="text-indigo-500" /></div>
                        <div className="text-xs sm:text-sm text-indigo-600 mb-1 break-words">{recipe.result.description}</div>
                        <div className="text-xs text-indigo-500 break-words">
                          {(recipe.ingredients.map((ing: string) => allItemsMap.get(ing)?.name || ing)).join(' + ')}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {Object.keys(CONSTANTS.LEGENDARY_UNLOCKS).some(key => inventory[CONSTANTS.LEGENDARY_UNLOCKS[key].id] > 0) && (
            <>
              <h3 className="text-base sm:text-lg font-bold text-text-primary mt-6 mb-2 border-t border-border-color pt-4 flex items-center gap-2">🌟 Legendary Recipes</h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {Object.entries(CONSTANTS.LEGENDARY_UNLOCKS).map(([key, legendary]) => {
                  const resultSprite = (ASSETS as any).items?.[legendary.id]?.path || (ASSETS as any).images?.defaultItem?.path;
                  return (
                    inventory[legendary.id] > 0 && (
                      <div
                        key={key}
                        className="p-3 rounded-lg border bg-gradient-to-r from-yellow-100 to-orange-100 border-yellow-400 flex items-start gap-3"
                      >
                        <img src={resultSprite} alt={legendary.name} className="w-10 h-10 sm:w-12 sm:h-12 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-orange-800 text-sm sm:text-base">🌟 {legendary.name}</div>
                          <div className="text-xs sm:text-sm text-orange-600 mb-1 break-words">{legendary.description}</div>
                          <div className="text-xs text-orange-500">Legendary Recipe</div>
                        </div>
                      </div>
                    )
                  )
                })}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RecipeBookModal;
